import { FC } from "react";
import { Box, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import CommonStyle from "Components/Style";
import ImgPath from "Constant/Imgs";

export type SortOrderType = "asc" | "desc";

export interface SortOptionType {
  label: string;
  value: string;
}

export interface SortStateType {
  sortBy: string;
  sortOrder: SortOrderType;
}

type SortBarProps = {
  options: SortOptionType[];
  sort: SortStateType;
  onChange: (sort: SortStateType) => void;
};

/**
 * The slim "Sort by" row that sits between a sidebar modal's tabs and its
 * list: a field dropdown, and one button that flips the direction.
 *
 * It only reports the choice. Fetching in that order is the section's job.
 */
const SortBar: FC<SortBarProps> = ({ options, sort, onChange }) => {
  const isAsc = sort.sortOrder == "asc";
  return (
    <Box sx={{ ...sxBar }}>
      <Box display="flex" alignItems="center">
        <Typography variant="subtitle1" mr={1}>
          Sort by:
        </Typography>
        <Select
          size="small"
          value={sort.sortBy}
          onChange={(event) =>
            onChange({ ...sort, sortBy: event.target.value })
          }
          sx={{ ...sxSelect }}
        >
          {options.map((el) => {
            return (
              <MenuItem key={el.value} value={el.value}>
                {el.label}
              </MenuItem>
            );
          })}
        </Select>
      </Box>
      <Tooltip
        arrow
        placement="top"
        title={isAsc ? "Ascending" : "Descending"}
      >
        <Box
          sx={{ ...CommonStyle.sxRoundIconsMainSm }}
          onClick={() =>
            onChange({ ...sort, sortOrder: isAsc ? "desc" : "asc" })
          }
        >
          {isAsc ? <ImgPath.SortAscIcon /> : <ImgPath.SortDescIcon />}
        </Box>
      </Tooltip>
    </Box>
  );
};

export default SortBar;

const sxBar = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
  mx: 2,
  py: 0.75,
  borderBottom: "1px solid",
  borderBottomColor: "custom.main",
};

const sxSelect = {
  minWidth: 140,
  fontSize: "0.85rem",
  "& .MuiSelect-select": {
    py: 0.4,
  },
};
